import React, { useEffect, useRef, useState } from 'react';
import { useContracts } from '../../../context/ContractContext';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loader from '../../common/Loader';
import InlineLoader from '../../common/InlineLoader';
import { toBN, formatUSDT } from '../../../utils/format';
import { useTxRunner } from './AdminHelpers';

export default function LevelOps() {
  const { views, mainW, viewsW } = useContracts();
  const { busy, run } = useTxRunner();
  const [loading, setLoading] = useState(true);
  const [pcts, setPcts] = useState(Array(20).fill(''));
  const [reqs, setReqs] = useState({ b: Array(20).fill(''), s: Array(20).fill(''), r: Array(20).fill('') });
  const [rewards, setRewards] = useState({ tb: Array(11).fill(''), ss: Array(11).fill('') });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const [curPcts, curReqs, curRewards] = await Promise.all([
          views.getLevelPcts(),
          views.getLevelReqs(),
          views.getRewardReqs()
        ]);
        if (!mountedRef.current) return;
        setPcts(curPcts.map((x) => x.toString()));
        setReqs({
          b: curReqs.b.map((x) => (Number(x.toString()) / 1e18).toString()),
          s: curReqs.s.map((x) => (Number(x.toString()) / 1e18).toString()),
          r: curReqs.r.map((x) => x.toString())
        });
        setRewards({
          tb: curRewards.tb.map((x) => (Number(x.toString()) / 1e18).toString()),
          ss: curRewards.ss.map((x) => (Number(x.toString()) / 1e18).toString())
        });
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true); load();
    return () => { mountedRef.current = false; };
  }, [views]);

  const updateOnePct = (i) => run(`Updating L${i + 1} pct`, () => mainW.setLevelPct(i, pcts[i]));
  const updateAllPcts = () => run('Updating all level %', () => viewsW.setLevelPcts(pcts));
  const updateOneReq = (i) => run(`Updating L${i + 1} req`, () => mainW.setLevelReq(i, toBN(reqs.b[i]), toBN(reqs.s[i]), reqs.r[i]));
  const updateOneReward = (i) => run(`Updating reward L${i + 1}`, () => mainW.setRewardReq(i, toBN(rewards.tb[i]), toBN(rewards.ss[i])));

  if (loading) return <InlineLoader size="lg" text="Loading level config..." />;

  return (
    <>
      {busy && <Loader text={busy} />}

      <Card title="Level Percentages (20 Levels)" className="mb-4">
        <div className="text-dim mb-3" style={{ fontSize: 11 }}>Values in basis points (100 = 1%, max 10000). Total distributed per ROI.</div>
        <div className="table-wrap table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Payout %</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {pcts.map((v, i) => (
                <tr key={i}>
                  <td><span className="level-num">L{i + 1}</span></td>
                  <td>
                    <input className="input" style={{ maxWidth: 200 }} type="number" value={v}
                      onChange={(e) => { const np = [...pcts]; np[i] = e.target.value; setPcts(np); }} />
                  </td>
                  <td className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => updateOnePct(i)}>UPDATE</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Button block variant="primary" onClick={updateAllPcts}>UPDATE ALL LEVEL %</Button>
        </div>
      </Card>

      <Card title="Level Requirements (Directs / Self / Biz)" className="mb-4">
        <div className="table-wrap table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Direct Biz ($)</th>
                <th>Self Stake ($)</th>
                <th>Directs</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {reqs.r.map((_, i) => (
                <tr key={i}>
                  <td><span className="level-num">L{i + 1}</span></td>
                  <td><input className="input" type="number" value={reqs.b[i]}
                    onChange={(e) => { const n = { ...reqs, b: [...reqs.b] }; n.b[i] = e.target.value; setReqs(n); }} /></td>
                  <td><input className="input" type="number" value={reqs.s[i]}
                    onChange={(e) => { const n = { ...reqs, s: [...reqs.s] }; n.s[i] = e.target.value; setReqs(n); }} /></td>
                  <td><input className="input" type="number" value={reqs.r[i]}
                    onChange={(e) => { const n = { ...reqs, r: [...reqs.r] }; n.r[i] = e.target.value; setReqs(n); }} /></td>
                  <td className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => updateOneReq(i)}>SET</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Reward Tier Requirements">
        <div className="table-wrap table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Team Biz ($)</th>
                <th>Self Topup ($)</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {rewards.tb.map((_, i) => (
                <tr key={i}>
                  <td><span className="level-num">L{i + 1}</span></td>
                  <td><input className="input" type="number" value={rewards.tb[i]}
                    onChange={(e) => { const n = { ...rewards, tb: [...rewards.tb] }; n.tb[i] = e.target.value; setRewards(n); }} /></td>
                  <td><input className="input" type="number" value={rewards.ss[i]}
                    onChange={(e) => { const n = { ...rewards, ss: [...rewards.ss] }; n.ss[i] = e.target.value; setRewards(n); }} /></td>
                  <td className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => updateOneReward(i)}>SET</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
